import type { NavScope } from '../constants/menu-route-permissions';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { SplashScreen } from 'src/components/loading-screen';

import { ForbiddenView } from 'src/sections/error';

import { useMyPermissions } from '../hooks/use-my-permissions';
import { resolveRoutePermission } from '../utils/resolve-route-permission';

// ----------------------------------------------------------------------

const SCOPE_HOME_PATH: Record<NavScope, string> = {
  dashboard: paths.dashboard.root,
  hq: paths.hq.overview,
};

type PermissionGuardProps = {
  /** 菜单作用域，决定使用哪套路由权限规则 */
  scope: NavScope;
  children: React.ReactNode;
};

/**
 * 页面 ACL 守卫
 *
 * 根据 menu-seed 定义的路由权限规则，校验当前用户是否拥有访问权限。
 * 须套在 AuthGuard、AdminGuard 之内（依赖已登录且权限集已加载）。
 */
export function PermissionGuard({ scope, children }: PermissionGuardProps) {
  const pathname = usePathname();
  const { hasPermission, isLoading } = useMyPermissions();

  if (CONFIG.auth.skip) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <SplashScreen />;
  }

  const requiredPermission = resolveRoutePermission(scope, pathname);

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <DashboardLayout showWorkspacesPopover navScope={scope}>
        <ForbiddenView backHref={SCOPE_HOME_PATH[scope]} embedded />
      </DashboardLayout>
    );
  }

  return <>{children}</>;
}
